/**
 * AccessLogModal - Detail modal for AccessLogsPage
 */
import React from 'react';
import { LogIn } from 'lucide-react';
import { AccessLog } from '../../services/accessLogService';
import { ActionBadge, StatusBadge } from './AccessLogBadges';

interface AccessLogModalProps {
  log: AccessLog;
  onClose: () => void;
  formatDate: (dateStr: string) => string;
}

export const AccessLogModal: React.FC<AccessLogModalProps> = ({ log, onClose, formatDate }) => {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <LogIn className="text-blue-600" />
            Detalhes do Log #{log.id}
          </h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Data/Hora</label>
              <p className="text-sm text-gray-900 dark:text-white">{formatDate(log.data_hora)}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ação</label>
              <ActionBadge acao={log.acao} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Usuário</label>
              <p className="text-sm text-gray-900 dark:text-white">{log.usuario || log.email_tentativa || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
              <StatusBadge sucesso={log.sucesso} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">IP</label>
              <p className="text-sm text-gray-900 dark:text-white font-mono">{log.ip || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Empresa</label>
              <p className="text-sm text-gray-900 dark:text-white">{log.empresa || '-'}</p>
            </div>
          </div>

          {log.motivo_falha && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Motivo da Falha</label>
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                {log.motivo_falha}
              </p>
            </div>
          )}

          {log.device_info && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Dispositivo</label>
              <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-2 rounded">
                <p>Browser: {log.device_info.browser || 'Unknown'}</p>
                <p>Sistema: {log.device_info.os || 'Unknown'}</p>
                <p>Tipo: {log.device_info.device_type || 'Unknown'}</p>
              </div>
            </div>
          )}

          {log.user_agent && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">User Agent</label>
              <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded font-mono break-all">
                {log.user_agent}
              </p>
            </div>
          )}
        </div>
        <div className="p-6 border-t dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
